import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { SelectButton } from "primereact/selectbutton";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import {
  useCreateOptionMutation,
  useLazyGetStationOptionsListQuery,
} from "../../../../redux/services/foodStationOptions";
import { allergiesOptions } from "../../../Student/OrderPage/constants/allergies";
import getSubdomain from "../../../../utils/getSubdomain";

import * as styles from "../MenuItems.module.css";

const LIST_PATH = "/app/manager/menu-items";

function OptionItemCreatePage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const stationId = searchParams.get("station");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const allergensMultiRef = useRef(null);

  const [fetchStationOptions, { data: stationOptions }] =
    useLazyGetStationOptionsListQuery();

  useEffect(() => {
    if (stationId) {
      fetchStationOptions({ id: stationId, subDomain: getSubdomain() });
    }
  }, [stationId, fetchStationOptions]);

  const groupEntry = useMemo(
    () =>
      stationOptions?.find((g) => String(g.group.group_id) === String(groupId)),
    [stationOptions, groupId],
  );

  const { control, handleSubmit } = useForm({
    defaultValues: {
      item_title: "",
      item_active: "Active",
      item_cost: "",
      item_note: "",
      item_allergens: [],
      item_wiki_url: "",
    },
  });

  const [
    createOption,
    { isSuccess: isCreateSuccess, isError: isCreateError, error: createError },
  ] = useCreateOptionMutation();

  useEffect(() => {
    if (isCreateSuccess) {
      showToast("success", "Option", "Option created!");
      navigate(LIST_PATH);
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    if (isCreateError) {
      showToast(
        "error",
        createError?.status,
        createError?.data?.message ||
          (createError?.data?.errors && "Incorrect data"),
      );
      setIsSubmitting(false);
    }
  }, [isCreateError]);

  const onCreate = (formData) => {
    if (isSubmitting) return;

    const cost =
      formData.item_cost !== null && formData.item_cost !== ""
        ? +formData.item_cost
        : 0;

    const nextOptionOrder = groupEntry?.items?.length
      ? Math.max(...groupEntry.items.map((o, idx) => o.order ?? idx + 1)) + 1
      : 1;

    createOption({
      data: {
        name: formData.item_title,
        visible: formData.item_active === "Active",
        cost,
        note: formData.item_note,
        allergens: formData.item_allergens || [],
        wiki_url: formData.item_wiki_url?.trim() || null,
        group_id: Number(groupId),
        order: nextOptionOrder,
      },
      subDomain: getSubdomain(),
    });
    setIsSubmitting(true);
  };

  const groupName = groupEntry?.group?.group_name;
  const title = groupName
    ? `New Menu Item Within ${groupName}`
    : "New Menu Item";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          disabled={isSubmitting}
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onCreate)}
        />
      }
    >
      <form>
        <Controller
          name="item_title"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Title</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                {...field}
                placeholder="Title"
              />
            </div>
          )}
        />

        <Controller
          name="item_wiki_url"
          control={control}
          rules={{
            validate: (value) => {
              if (!value) return true;
              try {
                const u = new URL(value.trim());
                if (u.protocol !== "http:" && u.protocol !== "https:") {
                  return "URL must start with http(s)://";
                }
                return true;
              } catch (_) {
                return "Enter a valid URL";
              }
            },
          }}
          render={({ field, fieldState }) => (
            <div className="field">
              <label>Wikipedia URL (optional)</label>
              <InputText
                className={`w-full mb-3 ${styles.input} ${
                  fieldState.invalid ? "p-invalid" : ""
                }`}
                {...field}
                value={field.value || ""}
                placeholder="https://en.wikipedia.org/wiki/Caesar_salad"
              />
              {fieldState.error && (
                <small className="p-error">{fieldState.error.message}</small>
              )}
            </div>
          )}
        />

        <Controller
          name="item_cost"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Cost</label>
              <InputNumber
                className={`w-full mb-3 ${styles.numberInput}`}
                minFractionDigits={2}
                maxFractionDigits={2}
                prefix="$"
                inputRef={field.ref}
                name={field.name}
                value={
                  field.value !== null && field.value !== ""
                    ? +field.value
                    : null
                }
                onBlur={field.onBlur}
                onChange={(e) => field.onChange(e.value)}
              />
            </div>
          )}
        />

        <Controller
          name="item_note"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Note</label>
              <div className={styles.textareaWrapper}>
                <InputTextarea
                  className={`w-full mb-3 ${styles.textarea}`}
                  {...field}
                />
              </div>
            </div>
          )}
        />

        <Controller
          name="item_allergens"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Allergens</label>
              <div className={styles.multiSelectWrapper}>
                <MultiSelect
                  ref={allergensMultiRef}
                  {...field}
                  options={allergiesOptions}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  placeholder="Select allergens"
                  className={`${styles.multiSelect} ${styles.input}`}
                  panelClassName={styles.multiSelectPanel}
                  panelStyle={{ maxHeight: "none" }}
                  showSelectAll={false}
                  onChange={(e) => {
                    const val = e.value;
                    if (Array.isArray(val)) {
                      if (val.includes("none") && val.length > 1) {
                        return field.onChange(["none"]);
                      }
                      return field.onChange(val);
                    }
                    return field.onChange(e);
                  }}
                />
              </div>
            </div>
          )}
        />

        <Controller
          name="item_active"
          control={control}
          render={({ field }) => (
            <SelectButton
              {...field}
              options={["Active", "Inactive"]}
              allowEmpty={false}
              className={styles.selectButton}
            />
          )}
        />
      </form>
    </EntityFormPage>
  );
}

export default OptionItemCreatePage;
